import { GeoFeature } from "../model";
import { BuildingType, buildingTypeFromString, ConstructionAge, constructionAgeFromString, RoofType, roofTypeFromString } from "../model";

export interface BuildingProps {
	name?: any;
	height?: any;
	heatDemand?: any;
	peakLoad?: any;
	warmWaterFraction?: any;
	roofType?: any;
	functionCode?: any;
	functionLabel?: any;
	function?: any; // backward compatibility
	type?: any;
	constructionAge?: any;
	groundArea?: any;
	country?: any;
	locality?: any;
	postalCode?: any;
	street?: any;
	streetNumber?: any;
	isHeated?: any;
	isSupplyCenter?: any;
	isIncluded?: any;
}

function stringOf(field: any): string {
	return typeof field === "string" ? field : "";
}

function floatOf(field: any): number {
	const t = typeof field;
	if (t === "number") {
		return field;
	}
	if (t === "string") {
		const f = parseFloat(field);
		return isNaN(f) ? 0 : f;
	}
	return 0;
}

function boolOf(field: any): boolean {
	const t = typeof field;
	if (t === "boolean") {
		return field;
	}
	if (t === "string") {
		return field.toLowerCase() === "true";
	}
	return false;
}

export class BuildingData {
	name: string;
	height: number;
	heatDemand: number;
	peakLoad: number;
	warmWaterFraction: number;
	roofType: RoofType;
	functionCode: string;
	functionLabel: string;
	type: BuildingType;
	constructionAge: ConstructionAge;
	groundArea: number;
	country: string;
	locality: string;
	postalCode: string;
	street: string;
	streetNumber: string;
	isHeated: boolean;
	isSupplyCenter: boolean;
	isIncluded: boolean;

	static of(f: GeoFeature): BuildingData {
		const props = f.properties || {};
		return new BuildingData(props);
	}

	constructor(d: BuildingData | BuildingProps) {
		if (d instanceof BuildingData) {
			this.name = d.name;
			this.height = d.height;
			this.heatDemand = d.heatDemand;
			this.peakLoad = d.peakLoad;
			this.warmWaterFraction = d.warmWaterFraction;
			this.roofType = d.roofType;
			this.functionCode = d.functionCode;
			this.functionLabel = d.functionLabel;
			this.type = d.type;
			this.constructionAge = d.constructionAge;
			this.groundArea = d.groundArea;
			this.country = d.country;
			this.locality = d.locality;
			this.postalCode = d.postalCode;
			this.street = d.street;
			this.streetNumber = d.streetNumber;
			this.isHeated = d.isHeated;
			this.isSupplyCenter = d.isSupplyCenter;
			this.isIncluded = d.isIncluded;
		} else {
			this.name = stringOf(d.name);
			this.height = floatOf(d.height);
			this.heatDemand = floatOf(d.heatDemand);
			this.peakLoad = floatOf(d.peakLoad);
			this.warmWaterFraction = floatOf(d.warmWaterFraction);
			this.roofType = roofTypeFromString(d.roofType);
			this.functionCode = stringOf(d.functionCode || d.function);
			this.functionLabel = stringOf(d.functionLabel || d.function);
			this.type = buildingTypeFromString(d.type || "MULTI_GENERATION");
			this.constructionAge = constructionAgeFromString(d.constructionAge || "AGE_1979_1995");
			this.groundArea = floatOf(d.groundArea);
			this.country = stringOf(d.country);
			this.locality = stringOf(d.locality);
			this.postalCode = stringOf(d.postalCode);
			this.street = stringOf(d.street);
			this.streetNumber = stringOf(d.streetNumber);
			this.isHeated = boolOf(d.isHeated);
			this.isSupplyCenter = boolOf(d.isSupplyCenter);
			this.isIncluded = boolOf(d.isIncluded);
		}
	}

	copyWith(props: BuildingProps): BuildingData {
		const copy = new BuildingData(this);
		if (props.name) {
			copy.name = props.name;
		}
		if (props.height) {
			copy.height = props.height;
		}
		if (props.heatDemand) {
			copy.heatDemand = props.heatDemand;
		}
		if (props.peakLoad) {
			copy.peakLoad = props.peakLoad;
		}
		if (props.warmWaterFraction) {
			copy.warmWaterFraction = props.warmWaterFraction;
		}
		if (props.roofType) {
			copy.roofType = props.roofType;
		}
		if (props.functionCode) {
			copy.functionCode = props.functionCode;
		}
		if (props.functionLabel) {
			copy.functionLabel = props.functionLabel;
		}
		if (props.type) {
			copy.type = buildingTypeFromString(props.type);
		}
		if (props.constructionAge) {
			copy.constructionAge = constructionAgeFromString(props.constructionAge);
		}
		if (props.groundArea) {
			copy.groundArea = props.groundArea;
		}
		if (props.country) {
			copy.country = props.country;
		}
		if (props.locality) {
			copy.locality = props.locality;
		}
		if (props.postalCode) {
			copy.postalCode = props.postalCode;
		}
		if (props.street) {
			copy.street = props.street;
		}
		if (props.streetNumber) {
			copy.streetNumber = props.streetNumber;
		}
		if (props.isHeated !== undefined) {
			copy.isHeated = props.isHeated;
		}
		if (props.isSupplyCenter !== undefined) {
			copy.isSupplyCenter = props.isSupplyCenter;
		}
		if (props.isIncluded !== undefined) {
			copy.isIncluded = boolOf(props.isIncluded);
		}
		return copy;
	}

	applyOn(f: GeoFeature) {
		if (!f.properties) {
			f.properties = {};
		}
		f.properties.name = this.name;
		f.properties.height = this.height;
		f.properties.heatDemand = this.heatDemand;
		f.properties.peakLoad = this.peakLoad;
		f.properties.warmWaterFraction = this.warmWaterFraction;
		f.properties.roofType = this.roofType;
		f.properties.functionCode = this.functionCode;
		f.properties.functionLabel = this.functionLabel;
		f.properties.type = this.type;
		f.properties.constructionAge = this.constructionAge;
		f.properties.groundArea = this.groundArea;
		f.properties.country = this.country;
		f.properties.locality = this.locality;
		f.properties.postalCode = this.postalCode;
		f.properties.street = this.street;
		f.properties.streetNumber = this.streetNumber;
		f.properties.isHeated = this.isHeated;
		f.properties.isSupplyCenter = this.isSupplyCenter;
		f.properties.isIncluded = this.isIncluded;
	}

	isValid(): boolean {
		return typeof this.name === "string" && this.name.trim().length > 0;
	}
}

export interface StreetProps {
	name?: any;
	isExcluded?: any;
}

export class StreetData {
	name: string;
	isExcluded: boolean;

	static of(f: GeoFeature): StreetData {
		const props = f.properties || {};
		return new StreetData(props);
	}

	constructor(d: StreetData | StreetProps) {
		if (d instanceof StreetData) {
			this.name = d.name;
			this.isExcluded = d.isExcluded;
		} else {
			this.name = stringOf(d.name);
			this.isExcluded = boolOf(d.isExcluded);
		}
	}

	copyWith(props: StreetProps): StreetData {
		const copy = new StreetData(this);
		if (props.name) {
			copy.name = props.name;
		}
		if (props.isExcluded !== undefined) {
			copy.isExcluded = boolOf(props.isExcluded);
		}
		return copy;
	}

	applyOn(f: GeoFeature) {
		if (!f.properties) {
			f.properties = {};
		}
		f.properties.name = this.name;
		f.properties.isExcluded = this.isExcluded;
	}

	isValid(): boolean {
		return typeof this.name === "string" && this.name.trim().length > 0;
	}
}
